import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ImagePlus, Film } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

// Uploads a photo OR a short video clip; reports progress; calls onUploaded({url, type}).
export default function MediaUpload({ onUploaded, testid = "media-upload" }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const maxMB = isVideo ? 50 : 8;
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(`${isVideo ? "Video" : "Photo"} must be under ${maxMB}MB`);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      const { data } = await api.post("/upload", fd, {
        onUploadProgress: (ev) => {
          if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      onUploaded({ url: data.url, type: data.type || (isVideo ? "video" : "image") });
      toast.success(isVideo ? "Clip added 🎬" : "Photo added 📸");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      <input ref={inputRef} type="file" accept="image/*,video/*" className="hidden" onChange={handle} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={progress !== null} data-testid={`${testid}-btn`}
        className="w-full rounded-2xl border-2 border-dashed border-slate-300 py-8 flex flex-col items-center gap-2 text-slate-500 hover:border-[#FF5A79] hover:text-[#FF5A79] transition disabled:opacity-60">
        {progress !== null ? (
          <>
            <Loader2 className="h-7 w-7 animate-spin" />
            <span className="text-sm font-semibold">Uploading… {progress}%</span>
          </>
        ) : (
          <>
            <div className="flex gap-2"><ImagePlus className="h-7 w-7" /><Film className="h-7 w-7" /></div>
            <span className="text-sm font-semibold">Add a photo or video clip</span>
            <span className="text-xs text-slate-400">Photos up to 8MB · videos up to 50MB</span>
          </>
        )}
      </button>
    </div>
  );
}
